import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './config';

const DEFAULT_JOBS = [
  {
    id: 'job-1',
    title: 'Goodshed Field Supervisor',
    department: 'Freight Operations',
    location: 'NFR Zone (Guwahati / Siliguri)',
    positionsCount: 5,
    eligibility: 'Graduate in any discipline / Higher Secondary with 2+ yrs railway freight experience',
    experience: '1–3 Years',
    type: 'Full-Time',
    description: 'Oversee daily goodshed workforce activity, monitor safety compliance, maintain shift logs, and coordinate loading/unloading logistics with railway authorities.',
    status: 'OPEN'
  },
  {
    id: 'job-2',
    title: 'Worker Registration & Welfare Officer',
    department: 'Welfare & HR',
    location: 'Zonal Office, Guwahati',
    positionsCount: 3,
    eligibility: 'MSW / MBA in HR / Graduate with social welfare outreach experience',
    experience: '2–4 Years',
    type: 'Full-Time',
    description: 'Facilitate ESIC/EPFO worker enrolment, issue QR identity cards, manage worker welfare grievances, and conduct health awareness camps at goodshed hubs.',
    status: 'OPEN'
  },
  {
    id: 'job-3',
    title: 'Digital Data & Portal Operator',
    department: 'IT & Digital Operations',
    location: 'Guwahati / New Delhi',
    positionsCount: 4,
    eligibility: 'B.Sc Computer Science / BCA / Any Graduate with computer typing certification',
    experience: '1–2 Years',
    type: 'Full-Time',
    description: 'Manage worker verification records, maintain real-time shift logs, update SGCL administrative portal, and assist field supervisors with digital reports.',
    status: 'OPEN'
  },
  {
    id: 'job-4',
    title: 'Safety & Quality Inspector',
    department: 'Safety & Quality Oversight',
    location: 'Multiple Freight Terminals (NFR)',
    positionsCount: 2,
    eligibility: 'Diploma / Degree in Industrial Safety or equivalent qualification',
    experience: '3+ Years',
    type: 'Full-Time',
    description: 'Conduct routine safety audits, enforce PPE compliance, monitor hazard prevention protocols, and conduct safety training sessions across goodshed yards.',
    status: 'OPEN'
  }
];

const Career = () => {
  const [jobs, setJobs] = useState(DEFAULT_JOBS);
  const [selectedJob, setSelectedJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [appliedJobTitle, setAppliedJobTitle] = useState('');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    qualification: '',
    experience: '1-2 Years',
    message: ''
  });
  const [resumeFile, setResumeFile] = useState(null);

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/careers/openings`);
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setJobs(data);
        }
      }
    } catch (err) {
      console.log('Using default jobs fallback:', err);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const handleOpenApplyModal = (job) => {
    setSelectedJob(job);
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      qualification: '',
      experience: job.experience || '1-2 Years',
      message: ''
    });
    setResumeFile(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setResumeFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = new FormData();
    payload.append('jobId', selectedJob ? selectedJob.id : 'general');
    payload.append('jobTitle', selectedJob ? selectedJob.title : 'General Application');
    payload.append('fullName', formData.fullName);
    payload.append('email', formData.email);
    payload.append('phone', formData.phone);
    payload.append('qualification', formData.qualification);
    payload.append('experience', formData.experience);
    payload.append('message', formData.message);

    if (resumeFile) {
      payload.append('resume', resumeFile);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/careers/apply`, {
        method: 'POST',
        body: payload
      });

      if (response.ok) {
        setAppliedJobTitle(selectedJob ? selectedJob.title : 'Position');
        setSelectedJob(null);
        setShowSuccessPopup(true);
      } else {
        const err = await response.json();
        alert(err.error || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Submission error:', error);
      // Fallback success response for demo
      setAppliedJobTitle(selectedJob ? selectedJob.title : 'Position');
      setSelectedJob(null);
      setShowSuccessPopup(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans pb-20">
      {/* Hero Banner */}
      <div className="relative text-white overflow-hidden" style={{ minHeight: '380px' }}>
        <img src="/SG (2).jpg" alt="Career at SGCL" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#001529] via-[#0d2d6b]/90 to-[#0d2d6b]/60" />
        <div className="absolute top-0 right-0 opacity-5 text-[180px] font-black select-none leading-none -mr-8 -mt-8">SGCL</div>

        <div className="relative z-10 px-4 md:px-20 py-16 max-w-4xl">
          <span className="bg-govt-orange text-white px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">
            Work With Us
          </span>
          <h1 className="text-4xl md:text-6xl font-black mt-4 mb-5 leading-tight uppercase italic">
            Career &amp; <span className="text-govt-orange">Opportunities</span>
          </h1>
          <p className="text-blue-100 font-bold border-l-4 border-govt-orange pl-5 italic max-w-2xl leading-relaxed text-base">
            Build a rewarding career with Shramjivi Goodshed Corporation Limited. Join our mission in transforming Indian Railways freight workforce management and infrastructure growth.
          </p>
        </div>

        {/* Tricolor border strip */}
        <div className="absolute bottom-0 left-0 right-0 h-1.5 flex">
          <div className="flex-1 bg-[#FF9933]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#138808]" />
        </div>
      </div>

      {/* Why Join SGCL */}
      <div className="container mx-auto px-4 md:px-20 mt-12">
        <div className="text-center mb-10">
          <span className="bg-orange-100 text-govt-orange px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Why SGCL?</span>
          <h2 className="text-3xl font-black text-govt-blue mt-4 uppercase italic">Empowering People &amp; Logistics</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: '🚀', title: 'National Impact', desc: 'Directly contribute to modernizing India\'s vital railway freight network and workforce welfare.' },
            { icon: '🛡️', title: 'Structured Environment', desc: 'Work under structured corporate governance, transparent policies, and statutory compliance.' },
            { icon: '🤝', title: 'Inclusive Growth', desc: 'Equal opportunity employer dedicated to worker dignity, skill enhancement, and career development.' }
          ].map((item, i) => (
            <div key={i} className="bg-white p-8 rounded-3xl shadow-md border border-slate-100 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 bg-orange-50 text-govt-orange rounded-2xl flex items-center justify-center text-3xl mb-5">
                {item.icon}
              </div>
              <h3 className="font-black text-govt-blue text-lg uppercase mb-2">{item.title}</h3>
              <p className="text-slate-500 text-xs font-semibold leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Openings Grid */}
      <div className="container mx-auto px-4 md:px-20 mt-16">
        <div className="text-center mb-10">
          <span className="bg-blue-100 text-govt-blue px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest">Current Openings</span>
          <h2 className="text-3xl font-black text-govt-blue mt-4 uppercase italic">Explore Active Job Roles</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {jobs.filter(j => j.status === 'OPEN').map((job) => (
            <div key={job.id} className="bg-white rounded-3xl p-8 shadow-lg border border-slate-200 hover:border-govt-orange transition-all duration-300 flex flex-col justify-between group">
              <div>
                {/* Header badges */}
                <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                  <span className="bg-govt-blue/10 text-govt-blue text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                    {job.department}
                  </span>
                  <div className="flex gap-2">
                    <span className="bg-orange-50 text-govt-orange border border-orange-200 text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full">
                      {job.positionsCount || 1} {job.positionsCount === 1 ? 'Position' : 'Positions'}
                    </span>
                    <span className="bg-green-500/10 text-green-700 text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-green-500/20">
                      {job.type || 'Full-Time'}
                    </span>
                  </div>
                </div>

                {/* Job Title & Location */}
                <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight group-hover:text-govt-orange transition-colors mb-2">
                  {job.title}
                </h3>
                <p className="text-xs font-bold text-slate-500 mb-5 flex items-center gap-1.5">
                  <span className="text-govt-orange">📍</span> {job.location}
                </p>

                {/* Requirements Pills Box */}
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 mb-6 space-y-2 text-xs font-semibold text-slate-700">
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Eligibility Criteria:</span>
                    <span className="font-bold text-slate-800">{job.eligibility}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 block">Experience Required:</span>
                    <span className="font-bold text-slate-800">{job.experience}</span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-slate-600 text-xs font-semibold leading-relaxed mb-6">
                  {job.description}
                </p>
              </div>

              {/* Apply CTA */}
              <button
                onClick={() => handleOpenApplyModal(job)}
                className="w-full py-3.5 bg-govt-orange text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-[0_4px_0_rgb(194,65,12)] hover:brightness-110 active:translate-y-1 transition-all"
              >
                Apply For Position →
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Candidate Application Form Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-slate-950/80 z-[100] flex items-center justify-center p-4 backdrop-blur-md overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-2xl w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 border border-slate-200 my-8">
            <div className="bg-govt-blue text-white p-6 flex justify-between items-center border-b-4 border-govt-orange">
              <div>
                <span className="bg-govt-orange text-white text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full mb-1 inline-block">
                  Job Application
                </span>
                <h3 className="text-xl md:text-2xl font-black uppercase tracking-tight">{selectedJob.title}</h3>
                <p className="text-xs text-blue-200 font-semibold mt-0.5">Location: {selectedJob.location}</p>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                className="text-white hover:text-govt-orange text-2xl font-bold ml-4"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue text-xs font-semibold text-slate-800"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue text-xs font-semibold text-slate-800"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue text-xs font-semibold text-slate-800"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">Highest Qualification</label>
                  <input
                    type="text"
                    name="qualification"
                    value={formData.qualification}
                    onChange={handleChange}
                    placeholder="e.g. B.Tech / Graduate / 12th Pass"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue text-xs font-semibold text-slate-800"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">Total Experience</label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="e.g. 2 Years in Freight / Fresher"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue text-xs font-semibold text-slate-800"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">Attach Resume / CV (PDF / DOC)</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-black file:bg-govt-blue file:text-white hover:file:bg-govt-orange transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block mb-1">Cover Note / Message</label>
                <textarea
                  name="message"
                  rows="3"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Briefly state your relevant skills or why you are suitable for this position..."
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-govt-blue text-xs font-semibold text-slate-800"
                ></textarea>
              </div>

              <div className="pt-3 flex gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedJob(null)}
                  className="w-1/3 py-3 rounded-xl border border-slate-200 text-xs font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-3 bg-govt-orange text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-md hover:brightness-110 active:scale-95 transition-all disabled:opacity-50"
                >
                  {submitting ? 'Submitting Application...' : 'Submit Application Now'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Application Success Popup Alert Modal */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-slate-950/85 z-[120] flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center shadow-2xl border border-slate-100 animate-in zoom-in-95 duration-300 relative overflow-hidden">
            <div className="w-20 h-20 bg-green-500/10 text-green-600 rounded-full flex items-center justify-center text-4xl mx-auto mb-5 border border-green-500/20">
              🎉
            </div>

            <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tight mb-2">
              Application Submitted Successfully!
            </h3>

            <p className="text-slate-600 text-xs font-semibold leading-relaxed mb-6">
              Thank you for applying for <span className="text-govt-orange font-bold">"{appliedJobTitle}"</span> at Shramjivi Goodshed Corporation Limited. Our HR recruitment team will review your application and resume and get back to you shortly.
            </p>

            <button
              onClick={() => setShowSuccessPopup(false)}
              className="w-full py-3.5 bg-govt-blue text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-govt-orange transition-colors shadow-md"
            >
              Great, Thank You
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Career;
